import { NotificationTemplateForm, NotificationTemplateTable } from '@today/ui-domain'
import { MESSAGE_TEMPLATE_VARIABLES, NOTIFICATION_BODY_MAX } from '@/modules/offer-notification/message-variables'
import { useNotificationTemplatesPage } from './useNotificationTemplatesPage'

type PageState = ReturnType<typeof useNotificationTemplatesPage>

/** The open create/edit form. */
function TemplateEditor({ page }: { page: PageState }) {
	if (!page.editing) return null
	return (
		<section aria-label={page.editing.id ? 'Edit template' : 'New template'} className="flex flex-col gap-3">
			<h2 className="text-lg font-semibold">{page.editing.id ? 'Edit template' : 'New template'}</h2>
			<NotificationTemplateForm
				draft={page.editing.draft}
				variables={MESSAGE_TEMPLATE_VARIABLES}
				maxBodyLength={NOTIFICATION_BODY_MAX}
				offerOptions={page.offerOptions}
				previewOfferId={page.previewOfferId}
				preview={page.preview.preview}
				previewError={page.preview.error}
				isPreviewing={page.preview.isPending}
				isSaving={page.isSaving}
				error={page.saveError}
				onChange={page.change}
				onPreviewOfferChange={page.setPreviewOfferId}
				onPreview={page.runPreview}
				onSave={page.save}
				onCancel={page.cancel}
			/>
		</section>
	)
}

/** The library: loading, refused, or the table. */
function TemplateLibrary({ page }: { page: PageState }) {
	const { library } = page
	if (library.loadFailed) {
		return (
			<div role="alert" className="flex flex-col items-start gap-3 text-sm text-ds-danger">
				<p>Could not load the notification templates.</p>
				<button type="button" onClick={library.reload} className="rounded-md border px-3 py-1">
					Try again
				</button>
			</div>
		)
	}
	if (library.loading) {
		return (
			<p className="text-sm text-muted-foreground" aria-live="polite">
				Loading the notification templates…
			</p>
		)
	}
	return (
		<NotificationTemplateTable
			rows={library.templates}
			pendingDeleteId={page.pendingDeleteId}
			onEdit={page.startEdit}
			onDeleteRequest={page.requestDelete}
			onDeleteConfirm={page.confirmDelete}
			onDeleteCancel={page.cancelDelete}
		/>
	)
}

/** `/t/:slug/admin/notification-templates` — the tenant's notification-template library (TO-140 REQ-011). */
export function NotificationTemplatesPage() {
	const page = useNotificationTemplatesPage()
	return (
		<section className="flex flex-col gap-6">
			<header className="flex items-center justify-between gap-4">
				<h1 className="text-xl font-semibold">Notification templates</h1>
				<button type="button" onClick={page.startCreate} className="rounded-md border px-3 py-1 text-sm">
					New template
				</button>
			</header>
			<TemplateEditor page={page} />
			<TemplateLibrary page={page} />
		</section>
	)
}
